import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, InputNumber, Slider, Tag, Tooltip, Modal, DatePicker, Input, Form, message as antdMessage } from "antd";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  BarChart2,
  CalendarClock,
  Loader2,
  Sparkles,
  FileText,
  Bell,
  EyeOff,
  Users,
  ClipboardList,
  Download,
  Lock,
  Unlock,
  Maximize2,
  Trash2,
} from "lucide-react";
import type { AllBuildingBlocks, ProgressPeriod, WMBT, VisibilityMap } from "../types";
import type { ProgressSettings } from "../types";
import { getCategoryStyle, getCategoryLabel } from "./BlockDetailView";
import { useMicRecorder, RecordingObjectType } from "@vojomo-core/shared";
import { renderMarkdown } from "utils/renderMarkdown";
import { exportTarPdf } from "utils/tarPdfExport";
import { VoInput } from "components/VoInput/VoInput";
import { ConsensusBuilderBar, type CommandCenterConfig } from "components/ConsensusBuilder/ConsensusBuilderBar";
import dayjs from "dayjs";
import { PAPPlanView } from "./PAPPlanView";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

interface ProgressReflectionPanelProps {
  sessionId: string;
  buildingBlocks: AllBuildingBlocks;
  yearStartMonth: number;
  yearStartDay?: number;
  isAdvisorOrOwner?: boolean;
  currentUserId?: string;
  /** Global Solo/Team mode (shared with the Roadmap tab). Optional so the
   *  build stays green before RoadmapBuilderSession wires it; defaults to
   *  solo / hidden until then. */
  viewMode?: "solo" | "team";
  onViewModeChange?: (v: "solo" | "team") => void;
  /** State-2 gate — when false the pill is hidden (forced solo upstream). */
  showViewToggle?: boolean;
  progressSettings?: ProgressSettings;
  visibilityMap?: VisibilityMap;
  companyName?: string;
  defaultProgressTab?: string;
  onReportCreated?: () => void;
  /** Optional: pipe progress note submissions into the main Vo chat */
  onSendToMainChat?: (message: string) => void;
  /** Called when the O/A toggles visibility so the parent can update session state */
  onVisibilityChange?: (updatedMap: VisibilityMap) => void;
}

interface SynthesisDoc {
  id?: string;
  reportMarkdown: string;
  perObjective: Array<{
    wmbt_index: number;
    objective_label: string;
    team_score: number;
    team_summary: string;
  }>;
  contributorCount: number;
  contributorNames?: string[];
  generatedAt: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPeriodLabel(period: ProgressPeriod, yearStartMonth?: number): string {
  if (period.periodType === "weekly" && yearStartMonth != null) {
    const { weekOf } = getWeekOfQuarter(period.periodStart, yearStartMonth);
    return `Week ${weekOf}`;
  }
  if (period.periodType !== "weekly") {
    const qNum = parseInt(period.periodKey.split("-Q")[1] || "1");
    return `Q${qNum}`;
  }
  const { weekOf } = getWeekOfQuarter(period.periodStart, yearStartMonth ?? 1);
  return `Week ${weekOf}`;
}

function formatPeriodDateRange(period: ProgressPeriod): string {
  const start = new Date(period.periodStart + "T00:00:00");
  const end = new Date(period.periodEnd + "T00:00:00");
  const fmt = (d: Date, includeYear = false) =>
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      ...(includeYear ? { year: "numeric" } : {}),
    });
  return `${fmt(start)} – ${fmt(end, true)}`;
}

function getAdjacentPeriodKey(periodKey: string, direction: 1 | -1): string {
  if (periodKey.includes("-W")) {
    const [yearStr, weekStr] = periodKey.split("-W");
    const year = parseInt(yearStr);
    const week = parseInt(weekStr);
    const jan4 = new Date(year, 0, 4);
    const srcIsoWeekday = (jan4.getDay() + 6) % 7;
    const mondayOfW1 = new Date(jan4);
    mondayOfW1.setDate(jan4.getDate() - srcIsoWeekday);
    const mondayOfWeek = new Date(mondayOfW1);
    mondayOfWeek.setDate(mondayOfW1.getDate() + (week - 1) * 7);
    const adjMonday = new Date(mondayOfWeek);
    adjMonday.setDate(mondayOfWeek.getDate() + direction * 7);
    const adjYear = adjMonday.getFullYear();
    const adjJan4 = new Date(adjYear, 0, 4);
    const adjIsoWeekday = (adjJan4.getDay() + 6) % 7;
    const mondayOfW1Adj = new Date(adjJan4);
    mondayOfW1Adj.setDate(adjJan4.getDate() - adjIsoWeekday);
    const diffWeeks = Math.round((adjMonday.getTime() - mondayOfW1Adj.getTime()) / (7 * 86400000));
    const adjWeek = diffWeeks + 1;
    return `${adjYear}-W${String(adjWeek).padStart(2, "0")}`;
  }
  if (periodKey.includes("-Q")) {
    const [yearStr, qStr] = periodKey.split("-Q");
    const year = parseInt(yearStr);
    const quarter = parseInt(qStr);
    const newQuarter = quarter + direction;
    if (newQuarter > 4) return `${year + 1}-Q1`;
    if (newQuarter < 1) return `${year - 1}-Q4`;
    return `${year}-Q${newQuarter}`;
  }
  return periodKey;
}

function getCurrentWeekKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const isoWeekday = (jan4.getDay() + 6) % 7;
  const mondayOfW1 = new Date(jan4);
  mondayOfW1.setDate(jan4.getDate() - isoWeekday);
  const diffWeeks = Math.floor((now.getTime() - mondayOfW1.getTime()) / (7 * 86400000));
  return `${year}-W${String(diffWeeks + 1).padStart(2, "0")}`;
}

function getFiscalQuarterBounds(
  d: Date,
  yearStartMonth: number
): { quarterStart: Date; quarterEnd: Date; quarterNum: number } {
  const monthNum = d.getMonth() + 1;
  const monthOffset = ((monthNum - yearStartMonth) + 12) % 12;
  const quarterNum = Math.floor(monthOffset / 3) + 1;
  const fqStartMonth = ((yearStartMonth - 1 + (quarterNum - 1) * 3) % 12) + 1;
  const fqStartYear = fqStartMonth > monthNum ? d.getFullYear() - 1 : d.getFullYear();
  const quarterStart = new Date(fqStartYear, fqStartMonth - 1, 1);
  const fqEndMonth0 = (fqStartMonth - 1 + 2) % 12;
  const fqEndYear = fqStartYear + (fqEndMonth0 < fqStartMonth - 1 ? 1 : 0);
  const quarterEnd = new Date(fqEndYear, fqEndMonth0 + 1, 0);
  return { quarterStart, quarterEnd, quarterNum };
}

function getWeekOfQuarter(
  periodStart: string,
  yearStartMonth: number
): { weekOf: number; totalWeeks: number } {
  const d = new Date(periodStart + "T00:00:00");
  const { quarterStart, quarterEnd } = getFiscalQuarterBounds(d, yearStartMonth);
  const quarterDays = (quarterEnd.getTime() - quarterStart.getTime()) / 86400000;
  const totalWeeks = Math.round(quarterDays / 7);
  const daysIntoQuarter = (d.getTime() - quarterStart.getTime()) / 86400000;
  const weekOf = Math.floor(daysIntoQuarter / 7) + 1;
  return { weekOf: Math.max(1, weekOf), totalWeeks: Math.max(1, totalWeeks) };
}

/**
 * Single source of truth for "on-track" threshold — used by BOTH the legend
 * and the individual score badge colors so they always agree.
 *
 * Weekly:  fraction of quarter elapsed this week × 100 (same formula as legend).
 * QBR current quarter: fraction of fiscal year elapsed today × 100 (0–100 scale).
 * QBR other quarters:  end-of-quarter target (qNum × 25, i.e. Q1=25, Q2=50, Q3=75, Q4=100).
 */
function computePaceThreshold(
  periodType: "weekly" | "qbr",
  periodKey: string,
  periodStart: string,
  yearStartMonth: number,
): number {
  if (periodType === "weekly") {
    const { weekOf, totalWeeks } = getWeekOfQuarter(periodStart, yearStartMonth);
    return (weekOf / totalWeeks) * 100;
  }
  // QBR — is this the current quarter?
  const now = new Date();
  const { quarterNum: curQ, quarterStart: curQStart } = getFiscalQuarterBounds(now, yearStartMonth);
  const currentQbrKey = `${curQStart.getFullYear()}-Q${curQ}`;
  if (periodKey === currentQbrKey) {
    const m = now.getMonth() + 1;
    const monthsIntoFY = ((m - yearStartMonth + 12) % 12);
    const dayFraction = (now.getDate() - 1) / 30;
    return Math.min((monthsIntoFY + dayFraction) / 12, 1) * 100;
  }
  const qNum = parseInt(periodKey.split("-Q")[1] || "1");
  return qNum * 25;
}

/** Color classes for the large score text in solo/team drawers. */
function scoreColor(score: number | null, paceThreshold: number): string {
  if (score === null) return "";
  if (score >= paceThreshold) return "text-emerald-600";
  if (score >= paceThreshold * 0.75) return "text-amber-500";
  if (score >= paceThreshold * 0.5) return "text-orange-500";
  return "text-red-500";
}

/** Ant Design Tag color for score badges. */
function scoreBadgeColor(score: number | null, paceThreshold: number): string {
  if (score === null) return "default";
  if (score >= paceThreshold) return "success";
  if (score >= paceThreshold * 0.75) return "warning";
  if (score >= paceThreshold * 0.5) return "orange";
  return "error";
}

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Markdown Renderer (shared utility) ────────────────────────────────────────
// Delegates to utils/renderMarkdown which handles h1–h3, bullets, tables, bold/italic.

function renderSimpleMarkdown(markdown: string) {
  return renderMarkdown(markdown, { baseFontSize: 11 });
}

// ── Pill Toggle ───────────────────────────────────────────────────────────────

// ── Period Navigator ──────────────────────────────────────────────────────────

function PeriodNavigator({
  period,
  onNavigate,
  isLoading,
  yearStartMonth,
  contextLabel,
  isAdvisorOrOwner,
  onToggleLock,
  periodStatus,
}: {
  period: ProgressPeriod;
  onNavigate: (key: string) => void;
  isLoading: boolean;
  yearStartMonth: number;
  contextLabel: string;
  isAdvisorOrOwner?: boolean;
  onToggleLock?: (newLocked: boolean) => void;
  periodStatus?: "locked" | "active" | "planning" | null;
}) {
  const prevKey = getAdjacentPeriodKey(period.periodKey, -1);
  const nextKey = getAdjacentPeriodKey(period.periodKey, 1);

  const isWeekly = period.periodType === "weekly";

  const currentKey = isWeekly
    ? getCurrentWeekKey()
    : (() => {
        const now = new Date();
        const { quarterNum, quarterStart } = getFiscalQuarterBounds(now, yearStartMonth);
        return `${quarterStart.getFullYear()}-Q${quarterNum}`;
      })();
  // Allow navigating up to one period ahead of current (next week or next quarter)
  const nextAllowedKey = getAdjacentPeriodKey(currentKey, 1);
  const isFuture = nextKey > nextAllowedKey;

  const weekInfo = isWeekly
    ? getWeekOfQuarter(period.periodStart, yearStartMonth)
    : null;

  // Use computePaceThreshold — the same function used by score badge colors.
  const paceThreshold = computePaceThreshold(
    isWeekly ? "weekly" : "qbr",
    period.periodKey,
    period.periodStart,
    yearStartMonth,
  );

  let thresholdLegend: React.ReactNode = null;
  if (isWeekly && weekInfo) {
    const expectedPct = Math.round(paceThreshold);
    const behindPct = Math.round(paceThreshold * 0.75);
    const farBehindPct = Math.round(paceThreshold * 0.5);
    thresholdLegend = (
      <div className="px-4 py-1 border-b bg-slate-50/60 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-semibold text-muted-foreground mr-0.5">
          {weekInfo.weekOf} of {weekInfo.totalWeeks}
        </span>
        <span className="text-[10px] text-muted-foreground/50 mr-1">·</span>
        <span className="text-[10px] text-muted-foreground mr-1">Quarterly Objectives</span>
        <span className="text-[10px] text-muted-foreground/50 mr-1">·</span>
        <Tag color="success" className="text-[10px] py-0">On Track ≥{expectedPct}%</Tag>
        <Tag color="warning" className="text-[10px] py-0">Behind ≥{behindPct}%</Tag>
        <Tag color="orange" className="text-[10px] py-0">Far Behind ≥{farBehindPct}%</Tag>
        <Tag color="error" className="text-[10px] py-0">Red Flag &lt;{farBehindPct}%</Tag>
      </div>
    );
  } else if (!isWeekly) {
    const qNum = parseInt(period.periodKey.split("-Q")[1] || "1");
    const expectedScore = Math.round(paceThreshold);
    const behindScore = Math.round(paceThreshold * 0.75);
    const farBehindScore = Math.round(paceThreshold * 0.5);
    thresholdLegend = (
      <div className="px-4 py-1 border-b bg-slate-50/60 flex items-center gap-1.5 flex-wrap">
        <span className="text-[10px] font-semibold text-muted-foreground mr-0.5">
          {qNum} of 4
        </span>
        <span className="text-[10px] text-muted-foreground/50 mr-1">·</span>
        <span className="text-[10px] text-muted-foreground mr-1">1-Year Objectives</span>
        <span className="text-[10px] text-muted-foreground/50 mr-1">·</span>
        <Tag color="success" className="text-[10px] py-0">On Track ≥{expectedScore}%</Tag>
        <Tag color="warning" className="text-[10px] py-0">Behind ≥{behindScore}%</Tag>
        <Tag color="orange" className="text-[10px] py-0">Far Behind ≥{farBehindScore}%</Tag>
        <Tag color="error" className="text-[10px] py-0">Red Flag &lt;{farBehindScore}%</Tag>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center px-2 py-1 border-b bg-muted/20 gap-2">
        <Button
          type="text"
          size="small"
          icon={<ChevronLeft className="h-4 w-4" />}
          onClick={() => onNavigate(prevKey)}
          disabled={isLoading}
          style={{ padding: "0 4px", flexShrink: 0 }}
        />
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <Tooltip title={formatPeriodDateRange(period)} placement="bottom">
            <span className="text-sm font-medium whitespace-nowrap cursor-default">{formatPeriodLabel(period, yearStartMonth)}</span>
          </Tooltip>
          {period.isFinalized && !isAdvisorOrOwner && (
            <Tooltip title="This period is locked">
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 shrink-0">
                <Lock className="h-3 w-3 text-amber-700" />
              </span>
            </Tooltip>
          )}
          {isAdvisorOrOwner && (
            <Tooltip title={period.isFinalized ? "Click to unlock this period" : "Click to lock this period"}>
              <button
                className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full border-0 outline-none cursor-pointer transition-all shrink-0 ${
                  period.isFinalized
                    ? "bg-amber-100 hover:bg-amber-200"
                    : "bg-transparent hover:bg-slate-100"
                }`}
                onClick={() => onToggleLock?.(!period.isFinalized)}
              >
                {period.isFinalized
                  ? <Lock className="h-3 w-3 text-amber-700" />
                  : <Unlock className="h-3 w-3 text-slate-400" />}
              </button>
            </Tooltip>
          )}
          {periodStatus === "active" && (
            <Tag color="blue" className="text-[10px] py-0 shrink-0">Active</Tag>
          )}
          {periodStatus === "planning" && (
            <Tag className="text-[10px] py-0 shrink-0" style={{ color: "#8c8c8c", borderColor: "#d9d9d9", background: "#fafafa" }}>Planning Ahead</Tag>
          )}
          <Tooltip title={contextLabel} placement="bottom">
            <button className="border-0 bg-transparent outline-none cursor-pointer p-0 text-muted-foreground/40 hover:text-primary/60 transition-colors shrink-0">
              <span className="text-[11px]">ⓘ</span>
            </button>
          </Tooltip>
          <Tooltip title={isFuture ? (isWeekly ? "Can't navigate beyond next week" : "Can't navigate beyond next quarter") : undefined}>
            <Button
              type="text"
              size="small"
              icon={<ChevronRight className="h-4 w-4" />}
              onClick={() => onNavigate(nextKey)}
              disabled={isLoading || isFuture}
              style={{ padding: "0 4px", flexShrink: 0 }}
            />
          </Tooltip>
        </div>
      </div>
      {thresholdLegend}
    </>
  );
}

// ── Full Report Drawer ────────────────────────────────────────────────────────

function FullReportDrawer({
  open,
  onClose,
  item,
  state,
  periodLabel,
  periodType: _periodType,
  paceThreshold,
}: {
  open: boolean;
  onClose: () => void;
  item: WMBT;
  state: ProgressPeriod["currentState"][string] | undefined;
  periodLabel: string;
  periodType: "weekly" | "qbr";
  paceThreshold: number;
}) {
  const catStyle = getCategoryStyle(item.category);

  const Section = ({ label, value, icon }: { label: string; value: string | null | undefined; icon: string }) => (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {icon} {label}
      </p>
      {value ? (
        <p className="text-sm leading-relaxed">{value}</p>
      ) : (
        <p className="text-sm text-muted-foreground/50 italic">Not captured yet</p>
      )}
    </div>
  );

  return (
    <Drawer
      title={
        <div className="flex items-center gap-2">
          <Tag
            className="text-[10px] py-0"
            style={{ borderColor: catStyle.border, color: catStyle.text, background: catStyle.bg }}
          >
            {getCategoryLabel(item.category)}
          </Tag>
          <span className="text-sm font-medium">
            {item.categoryObjective || item.label || "Progress Report"}
          </span>
        </div>
      }
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      styles={{ body: { padding: 24 } }}
    >
      <div className="space-y-5">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Period</p>
          <p className="text-sm font-medium">{periodLabel}</p>
        </div>

        {state?.pctComplete != null && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Progress Score</p>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${scoreColor(state.pctComplete, paceThreshold)}`}>
                {`${Math.round(state.pctComplete)}%`}
              </span>
            </div>
          </div>
        )}

        <div className="border-t pt-4 space-y-4">
          <Section label="Rationale" value={state?.rationale} icon="📊" />
          <Section label="What's Working" value={state?.whatsWorking} icon="✅" />
          <Section label="Challenges" value={state?.challenges} icon="⚡" />
          <Section label="Focused Progress Actions" value={state?.focusAhead} icon="🎯" />
        </div>

        {state?.lastUpdatedByName && state?.lastUpdatedAt && (
          <p className="text-[10px] text-muted-foreground/60 border-t pt-3">
            Last updated by {state.lastUpdatedByName} · {formatRelativeTime(state.lastUpdatedAt)}
          </p>
        )}
      </div>
    </Drawer>
  );
}

// ── Contributor Report Drawer ─────────────────────────────────────────────────

function ContributorReportDrawer({
  open,
  onClose,
  contributorName,
  periodLabel,
  periodType: _periodType,
  objectiveLabel,
  score,
  notes,
  paceThreshold,
}: {
  open: boolean;
  onClose: () => void;
  contributorName: string;
  periodLabel: string;
  periodType: "weekly" | "qbr";
  objectiveLabel: string;
  score: number | null;
  notes: {
    rationale?: string | null;
    whats_working?: string | null;
    challenges?: string | null;
    focus_ahead?: string | null;
  };
  paceThreshold: number;
}) {
  const Section = ({ label, value, icon }: { label: string; value: string | null | undefined; icon: string }) => (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        {icon} {label}
      </p>
      {value ? (
        <p className="text-sm leading-relaxed">{value}</p>
      ) : (
        <p className="text-sm text-muted-foreground/50 italic">Not captured yet</p>
      )}
    </div>
  );

  return (
    <Drawer
      title={
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold">{contributorName}</span>
          <span className="text-xs text-muted-foreground font-normal">{objectiveLabel}</span>
        </div>
      }
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      styles={{ body: { padding: 24 } }}
    >
      <div className="space-y-5">
        <div>
          <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Period</p>
          <p className="text-sm font-medium">{periodLabel}</p>
        </div>

        {score != null && (
          <div>
            <p className="text-[10px] text-muted-foreground uppercase tracking-wide mb-2">Progress Score</p>
            <div className="flex items-center gap-2">
              <span className={`text-2xl font-bold ${scoreColor(score, paceThreshold)}`}>
                {`${Math.round(score)}%`}
              </span>
            </div>
          </div>
        )}

        <div className="border-t pt-4 space-y-4">
          <Section label="Rationale" value={notes.rationale} icon="📊" />
          <Section label="What's Working" value={notes.whats_working} icon="✅" />
          <Section label="Challenges" value={notes.challenges} icon="⚡" />
          <Section label="Focused Progress Actions" value={notes.focus_ahead} icon="🎯" />
        </div>
      </div>
    </Drawer>
  );
}

// ── WMBT Accordion Row ────────────────────────────────────────────────────────

function WMBTAccordionRow({
  item,
  index,
  state,
  periodId,
  periodLabel,
  sessionId,
  periodType,
  onPeriodUpdate,
  isOpen,
  onToggle,
  contributionMode,
  onContributionSave,
  measurements,
  paceThreshold,
  onSendToChat,
  readOnly,
}: {
  item: WMBT;
  index: number;
  state: ProgressPeriod["currentState"][string] | undefined;
  periodId: string;
  periodLabel: string;
  sessionId: string;
  periodType: "weekly" | "qbr";
  onPeriodUpdate: (updated: ProgressPeriod) => void;
  isOpen: boolean;
  onToggle: () => void;
  contributionMode?: { periodType: string; periodKey: string };
  onContributionSave?: (index: number, update: Record<string, unknown>) => void;
  measurements?: Array<{ label: string; value: string | null }>;
  paceThreshold: number;
  onSendToChat?: (message: string) => void;
  readOnly?: boolean;
}) {
  const catStyle = getCategoryStyle(item.category);
  // Score scale is always 0–100% regardless of period type.
  const SCORE_MAX = 100;
  const SCORE_STEP = 1;
  const formatScore = (x: number) => `${Math.round(x)}%`;

  const [localPct, setLocalPct] = useState<number>(state?.pctComplete ?? 0);
  const [rawInput, setRawInput] = useState("");
  const [isParsing, setIsParsing] = useState(false);
  const [isSavingPct, setIsSavingPct] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { isRecording, startRecording, stopRecording, isInitializing, isProcessingAudio } =
    useMicRecorder();

  useEffect(() => {
    setLocalPct(state?.pctComplete ?? 0);
  }, [state?.pctComplete]);

  const pctRef = useRef(localPct);
  pctRef.current = localPct;

  const savePct = async (value: number) => {
    setIsSavingPct(true);
    try {
      if (contributionMode) {
        const r = await fetch(
          `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${contributionMode.periodType}/${contributionMode.periodKey}/save`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ state: { [String(index)]: { pct_complete: value } } }),
            credentials: "include",
          }
        );
        if (r.ok) {
          const saved = await r.json();
          onContributionSave?.(index, { pct_complete: value, ...(saved.state?.[String(index)] ?? {}) });
        }
      } else {
        const r = await fetch(
          `${SERVER_URL}/api/rb/sessions/${sessionId}/progress/${periodId}/update`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ wmbt_index: index, pct_complete: value }),
            credentials: "include",
          }
        );
        if (r.ok) onPeriodUpdate(await r.json());
      }
    } finally {
      setIsSavingPct(false);
    }
  };

  const handleParseAndSave = async (overrideText?: string) => {
    const text = overrideText ?? rawInput;
    if (!text.trim()) return;
    setIsParsing(true);
    try {
      if (contributionMode) {
        // Contributors: use AI parse-and-save endpoint (same Vo analysis as owners get)
        const r = await fetch(
          `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${contributionMode.periodType}/${contributionMode.periodKey}/parse-and-save`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              wmbt_index: index,
              pct_complete: localPct,
              raw_text: text,
              objective_label: item.categoryObjective || item.label || "Unnamed objective",
            }),
            credentials: "include",
          }
        );
        if (r.ok) {
          const saved = await r.json();
          onContributionSave?.(index, saved.state?.[String(index)] ?? {});
          const objectiveText = item.categoryObjective || item.label || "Unnamed objective";
          const scoreText = formatScore(localPct);
          onSendToChat?.(
            `Progress update — ${getCategoryLabel(item.category)}: ${objectiveText}\nScore: ${scoreText}\nNotes: ${text}`
          );
          setRawInput("");
        } else {
          antdMessage.error("Failed to save progress notes — please try again.");
        }
      } else {
        const r = await fetch(
          `${SERVER_URL}/api/rb/sessions/${sessionId}/progress/${periodId}/parse-and-save`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              wmbt_index: index,
              pct_complete: localPct,
              raw_text: text,
              objective_label: item.label || item.categoryObjective || "Unnamed objective",
            }),
            credentials: "include",
          }
        );
        if (r.ok) {
          onPeriodUpdate(await r.json());
          const objectiveText = item.categoryObjective || item.label || "Unnamed objective";
          const scoreText = formatScore(localPct);
          onSendToChat?.(
            `Progress update — ${getCategoryLabel(item.category)}: ${objectiveText}\nScore: ${scoreText}\nNotes: ${text}`
          );
          setRawInput("");
        } else {
          antdMessage.error("Failed to save progress notes — please try again.");
        }
      }
    } catch (e) {
      console.error("[VoInput] handleParseAndSave error", e);
      antdMessage.error("Network error saving progress notes — please try again.");
    } finally {
      setIsParsing(false);
    }
  };

  const savedPct = state != null ? (state.pctComplete ?? 0) : null;
  const scoreDisplay = savedPct !== null ? formatScore(savedPct) : null;

  const hasAnalysis = !!(state?.rationale || state?.whatsWorking || state?.challenges || state?.focusAhead);

  return (
    <>
      <div
        className="rounded-md border border-l-4 overflow-hidden"
        style={{ borderLeftColor: catStyle.text }}
      >
        <div
          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 transition-colors select-none"
          onClick={onToggle}
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <Tag
                className="text-[10px] py-0 shrink-0"
                style={{ borderColor: catStyle.border, color: catStyle.text, background: catStyle.bg }}
              >
                {getCategoryLabel(item.category)}
              </Tag>
              <span className="text-sm font-medium leading-tight line-clamp-2">
                {item.categoryObjective || item.label || "Unnamed objective"}
              </span>
            </div>
            {measurements && measurements.length > 0 && (
              <p className="text-[10px] text-muted-foreground/50 truncate leading-tight mb-0.5">
                {measurements.map((m) => (m.label + (m.value ? ` → ${m.value}` : ""))).join(" · ")}
              </p>
            )}
            <p className="text-xs truncate leading-tight">
              {state?.rationale
                ? <span className="text-muted-foreground">{state.rationale}</span>
                : <em className="text-muted-foreground/50">Add progress update</em>
              }
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {scoreDisplay ? (
              <Tag
                color={scoreBadgeColor(savedPct, paceThreshold)}
                className="text-[10px] py-0 font-semibold"
              >
                {scoreDisplay}
              </Tag>
            ) : (
              <span className="text-[10px] text-muted-foreground/50">—</span>
            )}
            {measurements && measurements.length > 0 && !isOpen && (
              <span className="text-[10px] text-muted-foreground/50">{measurements.length} measures</span>
            )}
            {isOpen
              ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/60" />
              : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/60" />
            }
          </div>
        </div>

        {isOpen && (
          <div className="px-3 pb-3 pt-2 space-y-3 border-t border-slate-100 bg-slate-50/40">
            {/* Measurements — shown first per Category+Objective+Measurement principle */}
            {measurements && measurements.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Measurements</p>
                <div className="space-y-1">
                  {measurements.map((m, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-slate-100/70 rounded px-2 py-1">
                      <span className="text-foreground/80">{m.label}</span>
                      {m.value && <span className="text-muted-foreground font-mono text-[10px]">{m.value}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Score — compact single row (no separate label) */}
            {readOnly ? (
              <div className="flex items-center gap-2">
                <Slider
                  value={localPct}
                  min={0}
                  max={SCORE_MAX}
                  step={SCORE_STEP}
                  disabled
                  style={{ flex: 1 }}
                />
                <span className="text-xs font-semibold text-muted-foreground whitespace-nowrap">
                  {formatScore(localPct)}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Slider
                  value={localPct}
                  min={0}
                  max={SCORE_MAX}
                  step={SCORE_STEP}
                  onChange={setLocalPct}
                  onChangeComplete={(v) => savePct(v)}
                  style={{ flex: 1 }}
                />
                <InputNumber
                  value={localPct}
                  min={0}
                  max={SCORE_MAX}
                  step={SCORE_STEP}
                  onChange={(v) => setLocalPct(v ?? 0)}
                  onBlur={() => savePct(pctRef.current)}
                  style={{ width: 72 }}
                  addonAfter="%"
                  size="small"
                />
                {isSavingPct && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground/50 shrink-0" />}
              </div>
            )}

            {/* Progress Notes — unified VoInput block (hidden in read-only) */}
            {!readOnly && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Progress assessment notes about this Objective</p>
                <VoInput
                  value={rawInput}
                  onChange={setRawInput}
                  onSubmit={handleParseAndSave}
                  isRecording={isRecording}
                  isInitializing={isInitializing}
                  isProcessingAudio={isProcessingAudio}
                  isSubmitting={isParsing}
                  onStartRecording={() =>
                    startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
                      const combined = rawInput ? `${rawInput} ${transcript}` : transcript;
                      setRawInput(combined);
                      // Auto-submit after recording, consistent with main Vo chat panel
                      await handleParseAndSave(combined);
                    })
                  }
                  onStopRecording={stopRecording}
                />
              </div>
            )}

            {hasAnalysis && (
              <div className="border rounded-md overflow-hidden">
                <div className="flex items-center justify-between px-3 py-1.5 bg-primary/5 border-b">
                  <div className="flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-primary" />
                    <span className="text-[10px] font-semibold text-primary">Vo's Analysis</span>
                  </div>
                  <Button
                    type="text"
                    size="small"
                    icon={<FileText className="h-3 w-3" />}
                    className="text-[10px] text-muted-foreground h-5"
                    style={{ padding: "0 4px" }}
                    onClick={(e) => { e.stopPropagation(); setDrawerOpen(true); }}
                  >
                    Full Report
                  </Button>
                </div>
                <div className="px-3 py-2 space-y-1.5">
                  {state?.rationale && (
                    <p className="text-xs">
                      <span className="font-medium text-muted-foreground">Rationale: </span>
                      <span className="text-foreground">{state.rationale}</span>
                    </p>
                  )}
                  {state?.whatsWorking && (
                    <p className="text-xs">
                      <span className="font-medium text-emerald-700">Working: </span>
                      <span className="text-foreground">{state.whatsWorking}</span>
                    </p>
                  )}
                  {state?.challenges && (
                    <p className="text-xs">
                      <span className="font-medium text-amber-700">Challenges: </span>
                      <span className="text-foreground">{state.challenges}</span>
                    </p>
                  )}
                  {state?.focusAhead && (
                    <p className="text-xs">
                      <span className="font-medium text-blue-700">Actions: </span>
                      <span className="text-foreground">{state.focusAhead}</span>
                    </p>
                  )}
                  {state?.lastUpdatedByName && state?.lastUpdatedAt && (
                    <p className="text-[10px] text-muted-foreground/60 pt-0.5">
                      {state.lastUpdatedByName} · {formatRelativeTime(state.lastUpdatedAt)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <FullReportDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        item={item}
        state={state}
        periodLabel={periodLabel}
        periodType={periodType}
        paceThreshold={paceThreshold}
      />
    </>
  );
}

// ── Period Tab ────────────────────────────────────────────────────────────────

function PeriodTab({
  sessionId,
  buildingBlocks,
  periodType,
  yearStartMonth,
  isAdvisorOrOwner,
  isVisible,
  currentUserId,
  viewMode,
  companyName,
  externalSynthRequest,
  onSynthStateChange,
  onReportCreated,
  onSendToChat,
  onActivePeriodChange,
  onPeriodStatusChange,
}: {
  sessionId: string;
  buildingBlocks: AllBuildingBlocks;
  periodType: "weekly" | "qbr";
  yearStartMonth: number;
  isAdvisorOrOwner: boolean;
  /** Per-period visibility toggle — true when this specific period is "open" */
  isVisible: boolean;
  currentUserId?: string;
  viewMode: "solo" | "team";
  companyName?: string;
  /** Incremented by parent to trigger synthesis from the ConsensusBuilderBar */
  externalSynthRequest?: number;
  /** Called with true when synthesis starts, false when complete/error */
  onSynthStateChange?: (synthesizing: boolean) => void;
  onReportCreated?: () => void;
  onSendToChat?: (message: string) => void;
  /** Called when the active period changes so parent can update visibility state */
  onActivePeriodChange?: (periodKey: string) => void;
  /** Called when the period status changes so parent can compute visibility defaults */
  onPeriodStatusChange?: (status: "locked" | "active" | "planning" | null) => void;
}) {
  const [period, setPeriod] = useState<ProgressPeriod | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});
  // Contributor-only: own saved contribution state (snake_case from API, keyed by wmbt index)
  const [ownContribState, setOwnContribState] = useState<Record<string, Record<string, unknown>>>({});
  const [lastLockedKey, setLastLockedKey] = useState<string | null>(null);

  const loadPeriod = useCallback(async (endpoint: string) => {
    setIsLoading(true);
    try {
      const r = await fetch(`${SERVER_URL}${endpoint}`, { credentials: "include" });
      if (r.ok) setPeriod(await r.json());
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadLockedKeys = useCallback(async () => {
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/progress/${periodType}/locked-keys`,
        { credentials: "include" }
      );
      if (r.ok) {
        const data: { lastLockedKey: string | null } = await r.json();
        setLastLockedKey(data.lastLockedKey);
      }
    } catch { /* ignore */ }
  }, [sessionId, periodType]);

  const loadOwnContribution = useCallback(async (pType: string, pKey: string) => {
    if (isAdvisorOrOwner) return;
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${pType}/${pKey}`,
        { credentials: "include" }
      );
      if (r.ok) {
        const list: Array<{ contributorUserId?: string; state: Record<string, Record<string, unknown>> }> = await r.json();
        // Find this user's own contribution — when visibility is "open", list may include others
        const mine = currentUserId
          ? (list.find((c) => c.contributorUserId === currentUserId) ?? list[0])
          : list[0];
        if (mine) setOwnContribState(mine.state ?? {});
      }
    } catch { /* ignore */ }
  }, [sessionId, isAdvisorOrOwner, currentUserId]);

  useEffect(() => {
    loadPeriod(`/api/rb/sessions/${sessionId}/progress/${periodType}/active`);
    loadLockedKeys();
  }, [sessionId, periodType, loadPeriod, loadLockedKeys]);

  useEffect(() => {
    if (period && !isAdvisorOrOwner) {
      loadOwnContribution(period.periodType, period.periodKey);
    }
  }, [period?.periodKey, isAdvisorOrOwner, loadOwnContribution]);

  // Lift active period key to parent for per-period visibility toggle
  useEffect(() => {
    if (period?.periodKey) onActivePeriodChange?.(period.periodKey);
  }, [period?.periodKey, onActivePeriodChange]);

  // Compute period status early (as useMemo) so it's available for effects
  const periodStatus: "locked" | "active" | "planning" | null = useMemo(() => {
    if (!period) return null;
    if (period.isFinalized) return "locked";
    if (lastLockedKey !== null) {
      const activeKey = getAdjacentPeriodKey(lastLockedKey, 1);
      const planKey = getAdjacentPeriodKey(activeKey, 1);
      if (period.periodKey === activeKey) return "active";
      if (period.periodKey === planKey) return "planning";
      return null;
    }
    const isWeekly = periodType === "weekly";
    const currentCalKey = isWeekly
      ? getCurrentWeekKey()
      : (() => {
          const now = new Date();
          const { quarterNum, quarterStart } = getFiscalQuarterBounds(now, yearStartMonth);
          return `${quarterStart.getFullYear()}-Q${quarterNum}`;
        })();
    const planKey = getAdjacentPeriodKey(currentCalKey, 1);
    if (period.periodKey === currentCalKey) return "active";
    if (period.periodKey === planKey) return "planning";
    return null;
  }, [period, lastLockedKey, periodType, yearStartMonth]);

  // Lift period status to parent for visibility default computation
  useEffect(() => {
    onPeriodStatusChange?.(periodStatus);
  }, [periodStatus, onPeriodStatusChange]);

  const handleNavigate = (periodKey: string) => {
    setExpandedRows({});
    setOwnContribState({});
    loadPeriod(`/api/rb/sessions/${sessionId}/progress/${periodType}/by-key/${periodKey}`);
  };

  const handleToggleLock = async (newLocked: boolean) => {
    if (!period) return;
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/progress/${periodType}/${period.periodKey}/lock`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_finalized: newLocked }),
        }
      );
      if (r.ok) {
        const updated = await r.json();
        setPeriod(updated);
        loadLockedKeys();
        antdMessage.success(newLocked ? "Period locked" : "Period unlocked");
      }
    } catch { /* ignore */ }
  };

  const blockKey = periodType === "weekly" ? "quarterlyObjectives" : "oneYearObjectives";
  const allWmbtItems: WMBT[] =
    (buildingBlocks[blockKey as keyof AllBuildingBlocks] as { wmbt?: WMBT[] })?.wmbt ?? [];
  const blockLabel = periodType === "weekly" ? "Quarterly Objectives" : "1-Year Objectives";

  // Group wmbt items by objective (categoryObjective), keeping ALL measurements per objective.
  // Header items (label=null, categoryObjective set) define the objective context for the
  // measurements that follow them — they are skipped as display rows.
  const objectiveGroups: Array<{ item: WMBT; originalIndex: number; measurements: Array<{ label: string; value: string | null }> }> = [];
  const seenObjectives = new Set<string>();
  let lastCategoryObjective: string | null = null;
  allWmbtItems.forEach((item, idx) => {
    const catObj = item.categoryObjective?.trim() ?? null;
    if (catObj) lastCategoryObjective = catObj;
    // Header items (no label) only update the objective context — they don't become rows
    if (!item.label?.trim()) return;
    const key = catObj || lastCategoryObjective || `${item.category}-${idx}`;
    if (!seenObjectives.has(key)) {
      seenObjectives.add(key);
      objectiveGroups.push({ item: { ...item, categoryObjective: key }, originalIndex: idx, measurements: [] });
    }
    const group = objectiveGroups[objectiveGroups.length - 1];
    if (group) {
      group.measurements.push({ label: item.label, value: item.value ?? null });
    }
  });
  const wmbtObjectives = objectiveGroups;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading period...</span>
      </div>
    );
  }

  if (!period) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-sm text-muted-foreground">Failed to load period data.</p>
      </div>
    );
  }

  // Pace threshold — same formula as the legend, single source of truth
  const paceThreshold = computePaceThreshold(periodType, period.periodKey, period.periodStart, yearStartMonth);

  if (allWmbtItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
        <p className="text-sm font-medium">No {blockLabel} yet</p>
        <p className="text-xs text-muted-foreground">
          Ask Vo to draft your {blockLabel.toLowerCase()} first, then come back here to track progress.
        </p>
      </div>
    );
  }

  const weekInfoForLabel = periodType === "weekly"
    ? getWeekOfQuarter(period.periodStart, yearStartMonth)
    : null;
  const qNumForLabel = periodType !== "weekly"
    ? parseInt(period.periodKey.split("-Q")[1] || "1")
    : null;

  const contextLabel = periodType === "qbr"
    ? `You're in Q${qNumForLabel} of 4 this year, tracking progress against your 1-Year Objectives. Save your scores and notes anytime during the quarter. After the quarter wraps up, your team lead runs a synthesis to produce the Team Alignment Report.`
    : `You're in Week ${weekInfoForLabel?.weekOf ?? "?"} of ${weekInfoForLabel?.totalWeeks ?? 13} in this quarter, tracking progress against your Quarterly Objectives. Save your scores and notes anytime during the week. After the week wraps up, your team lead runs a synthesis to produce the Team Alignment Report.`;

  return (
    <div className="flex flex-col h-full">
      <PeriodNavigator
        period={period}
        onNavigate={(key) => { setExpandedRows({}); handleNavigate(key); }}
        isLoading={isLoading}
        yearStartMonth={yearStartMonth}
        contextLabel={contextLabel}
        isAdvisorOrOwner={isAdvisorOrOwner}
        onToggleLock={handleToggleLock}
        periodStatus={periodStatus}
      />
      {viewMode === "team" ? (
        <div className="flex-1 overflow-y-auto">
          <TeamGridView
            sessionId={sessionId}
            periodType={periodType}
            periodKey={period.periodKey}
            isAdvisorOrOwner={isAdvisorOrOwner}
            isVisible={isVisible}
            periodLabel={formatPeriodLabel(period, yearStartMonth)}
            companyName={companyName}
            onReportCreated={onReportCreated}
            paceThreshold={paceThreshold}
            externalSynthRequest={externalSynthRequest}
            onSynthStateChange={onSynthStateChange}
          />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-3 pb-3 pt-1 space-y-1.5">
          {period.isFinalized && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 mb-1">
              <Lock className="h-3.5 w-3.5 text-amber-600 shrink-0" />
              <span className="text-xs text-amber-800">
                {formatPeriodLabel(period, yearStartMonth)} is locked — view only
              </span>
            </div>
          )}
          {wmbtObjectives.map(({ item, originalIndex, measurements: rowMeasurements }) => {
            const idx = originalIndex;
            // Convert own contribution entry (snake_case) to the same camelCase shape as period.currentState
            const contribEntry = ownContribState[String(idx)];
            const contribStateEntry: ProgressPeriod["currentState"][string] | undefined = contribEntry
              ? {
                  pctComplete: contribEntry.pct_complete as number | null,
                  rationale: contribEntry.rationale as string | null,
                  whatsWorking: contribEntry.whats_working as string | null,
                  challenges: contribEntry.challenges as string | null,
                  focusAhead: contribEntry.focus_ahead as string | null,
                  lastUpdatedBy: undefined,
                  lastUpdatedByName: undefined,
                  lastUpdatedAt: contribEntry.last_saved_at as string | undefined,
                }
              : undefined;

            const rowState = isAdvisorOrOwner ? period.currentState[String(idx)] : contribStateEntry;
            const contribMode = !isAdvisorOrOwner && period
              ? { periodType: period.periodType, periodKey: period.periodKey }
              : undefined;

            return (
              <WMBTAccordionRow
                key={idx}
                item={item}
                index={idx}
                state={rowState}
                periodLabel={formatPeriodLabel(period, yearStartMonth)}
                periodId={period.id}
                sessionId={sessionId}
                periodType={periodType}
                onPeriodUpdate={setPeriod}
                isOpen={expandedRows[idx] === true}
                onToggle={() => setExpandedRows((prev) => {
                  const isCurrentlyOpen = prev[idx] === true;
                  // Single-expand: opening a row collapses all others
                  return isCurrentlyOpen ? { ...prev, [idx]: false } : { [idx]: true };
                })}
                contributionMode={contribMode}
                onContributionSave={(i, update) => {
                  setOwnContribState((prev) => ({
                    ...prev,
                    [String(i)]: { ...(prev[String(i)] ?? {}), ...update },
                  }));
                }}
                measurements={rowMeasurements}
                paceThreshold={paceThreshold}
                onSendToChat={onSendToChat}
                readOnly={period.isFinalized}
              />
            );
          })}

          {/* Solo, per-user conversation with Vo about this period's progress.
              Available in BOTH State 1 and State 2 (not gated on the team
              command-bar controls) — every individual can talk to Vo. */}
          <PeriodConversation
            sessionId={sessionId}
            periodType={periodType}
            periodKey={period.periodKey}
            periodLabel={formatPeriodLabel(period, yearStartMonth)}
          />
        </div>
      )}
    </div>
  );
}

// ── Period Conversation (solo "Talk with Vo about your progress") ─────────────
//
// A per-user, period-level conversational surface. Unlike the team command-bar
// controls (visibility / notify / synthesize), this is solo content — every
// individual can talk to Vo about their own progress in BOTH State 1 and
// State 2, so it is NOT gated on showViewToggle. It POSTs to the period chat
// endpoint and consumes the SSE stream using the same reader/decoder/`data: `
// line-parsing approach as TeamGridView.handleSynthesize.

interface ProgressChatTurn {
  role: "user" | "assistant";
  content: string;
  createdAt?: string;
}

function PeriodConversation({
  sessionId,
  periodType,
  periodKey,
  periodLabel,
}: {
  sessionId: string;
  periodType: "weekly" | "qbr";
  periodKey: string;
  periodLabel: string;
}) {
  const [turns, setTurns] = useState<ProgressChatTurn[]>([]);
  const [input, setInput] = useState("");
  const [streamingText, setStreamingText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { isRecording, startRecording, stopRecording, isInitializing, isProcessingAudio } =
    useMicRecorder();

  const scrollRef = useRef<HTMLDivElement>(null);

  const chatBase = `${SERVER_URL}/api/rb/sessions/${sessionId}/progress/${periodType}/${periodKey}/chat`;

  // Load prior conversation on mount + whenever the period changes.
  useEffect(() => {
    let cancelled = false;
    setTurns([]);
    setStreamingText("");
    setError(null);
    (async () => {
      try {
        const r = await fetch(chatBase, { credentials: "include" });
        if (r.ok && !cancelled) {
          const history: ProgressChatTurn[] = await r.json();
          if (Array.isArray(history)) setTurns(history);
        }
      } catch { /* ignore — empty conversation is fine */ }
    })();
    return () => { cancelled = true; };
  }, [chatBase]);

  // Keep the transcript scrolled to the latest message while streaming.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, streamingText]);

  const handleSend = async (overrideText?: string) => {
    const content = (overrideText ?? input).trim();
    if (!content || isStreaming) return;
    setExpanded(true);
    setInput("");
    setError(null);
    setTurns((prev) => [...prev, { role: "user", content }]);
    setIsStreaming(true);
    setStreamingText("");

    let assembled = "";
    try {
      const r = await fetch(chatBase, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content }),
      });
      if (!r.ok || !r.body) {
        setIsStreaming(false);
        setError("Vo couldn't respond right now — please try again.");
        return;
      }
      // Mirror TeamGridView.handleSynthesize SSE consumption exactly.
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.content) {
              assembled += payload.content;
              setStreamingText(assembled);
            }
            if (payload.done) {
              setTurns((prev) => [...prev, { role: "assistant", content: assembled }]);
              setStreamingText("");
              setIsStreaming(false);
            }
            if (payload.error) {
              setIsStreaming(false);
              setStreamingText("");
              setError(payload.error);
            }
          } catch { /* ignore malformed line */ }
        }
      }
      // Stream ended without an explicit {done} — flush whatever we have.
      if (assembled) {
        setTurns((prev) =>
          prev.length && prev[prev.length - 1].role === "assistant" && prev[prev.length - 1].content === assembled
            ? prev
            : [...prev, { role: "assistant", content: assembled }]
        );
      }
      setStreamingText("");
      setIsStreaming(false);
    } catch {
      setIsStreaming(false);
      setStreamingText("");
      setError("Network error talking to Vo — please try again.");
    }
  };

  const hasConversation = turns.length > 0 || isStreaming || streamingText.length > 0;

  return (
    <div className="mt-2 rounded-lg border border-primary/15 bg-primary/[0.02] overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border-0 bg-transparent outline-none cursor-pointer text-left hover:bg-primary/[0.03] transition-colors"
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-xs font-semibold text-primary truncate">
            Talk with Vo about your progress this {periodType === "qbr" ? "quarter" : "week"}
          </span>
          {hasConversation && !expanded && (
            <span className="text-[10px] text-muted-foreground/60 shrink-0">
              · {turns.filter((t) => t.role === "user").length} message
              {turns.filter((t) => t.role === "user").length === 1 ? "" : "s"}
            </span>
          )}
        </div>
        {expanded
          ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />
          : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/60 shrink-0" />}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2.5 border-t border-primary/10">
          {!hasConversation && (
            <p className="text-[11px] text-muted-foreground/70 leading-relaxed pt-1">
              Vo has your {periodLabel} objectives and scores. Ask for an honest read on how
              you're tracking, where you're behind, or what to focus on next.
            </p>
          )}

          {hasConversation && (
            <div ref={scrollRef} className="max-h-72 overflow-y-auto space-y-2 pr-0.5">
              {turns.map((turn, i) => (
                turn.role === "user" ? (
                  <div key={i} className="flex justify-end">
                    <div className="max-w-[85%] rounded-lg rounded-br-sm bg-primary/10 px-2.5 py-1.5 text-xs text-foreground whitespace-pre-wrap break-words">
                      {turn.content}
                    </div>
                  </div>
                ) : (
                  <div key={i} className="flex justify-start">
                    <div className="max-w-[92%] rounded-lg rounded-bl-sm bg-white border border-slate-100 px-2.5 py-1.5">
                      {renderSimpleMarkdown(turn.content)}
                    </div>
                  </div>
                )
              ))}
              {isStreaming && (
                <div className="flex justify-start">
                  <div className="max-w-[92%] rounded-lg rounded-bl-sm bg-white border border-slate-100 px-2.5 py-1.5">
                    {streamingText
                      ? renderSimpleMarkdown(streamingText)
                      : (
                        <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground/60">
                          <Loader2 className="h-3 w-3 animate-spin" /> Vo is thinking…
                        </span>
                      )}
                  </div>
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-[11px] text-red-500">{error}</p>
          )}

          <VoInput
            value={input}
            onChange={setInput}
            onSubmit={handleSend}
            isRecording={isRecording}
            isInitializing={isInitializing}
            isProcessingAudio={isProcessingAudio}
            isSubmitting={isStreaming}
            placeholder="Or type a question about your progress…"
            onStartRecording={() =>
              startRecording(RecordingObjectType.MODUS_CHAT, async (transcript) => {
                const combined = input ? `${input} ${transcript}` : transcript;
                setInput(combined);
                await handleSend(combined);
              })
            }
            onStopRecording={stopRecording}
          />
        </div>
      )}
    </div>
  );
}

// ── Coming Soon Placeholder ───────────────────────────────────────────────────

function ComingSoonTab({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
      <Tag color="default" className="text-xs">Coming Soon</Tag>
      <p className="text-sm font-medium">{title}</p>
      <p className="text-xs text-muted-foreground max-w-xs">{description}</p>
    </div>
  );
}

// ── Team Grid View ────────────────────────────────────────────────────────────

interface GridData {
  periodType: string;
  periodKey: string;
  gridReleased: boolean;
  synthesisReleased: boolean;
  isPrivileged: boolean;
  canSeeAll: boolean;
  wmbtItems: Array<{ category: string; label?: string; value?: string; categoryObjective?: string }>;
  contributors: Array<{ userId: string; name: string; updatedAt: string | null }>;
  grid: Record<string, Record<string, {
    pct_complete?: number | null;
    rationale?: string | null;
    whats_working?: string | null;
    challenges?: string | null;
    focus_ahead?: string | null;
  }>>;
}

interface SynthReport {
  generated_at: string;
  report_markdown: string;
}

interface PeriodMetaDoc {
  id: string;
  periodType: string;
  periodKey: string;
  kickedOffByName: string;
  invitedParticipants: { name: string; email: string; status: string }[];
  deadline: string | null;
  personalMessage: string | null;
  status: string;
  createdAt: string;
  gridReleased: boolean;
  synthesisReleased: boolean;
  synthesisReports: SynthReport[];
}

function TeamGridView({
  sessionId,
  periodType,
  periodKey,
  isAdvisorOrOwner,
  isVisible,
  periodLabel,
  companyName,
  onReportCreated,
  paceThreshold,
  externalSynthRequest,
  onSynthStateChange,
}: {
  sessionId: string;
  periodType: "weekly" | "qbr";
  periodKey: string;
  isAdvisorOrOwner: boolean;
  /** Session-level visibility toggle — true when "open", false when "gated" */
  isVisible: boolean;
  periodLabel: string;
  companyName?: string;
  onReportCreated?: () => void;
  paceThreshold: number;
  externalSynthRequest?: number;
  onSynthStateChange?: (synthesizing: boolean) => void;
}) {
  const [gridData, setGridData] = useState<GridData | null>(null);
  const [, setActivePeriodMeta] = useState<PeriodMetaDoc | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [synthHistory, setSynthHistory] = useState<SynthesisDoc[]>([]);
  const [synthStreaming, setSynthStreaming] = useState(false);
  const [synthText, setSynthText] = useState("");
  const [synthError, setSynthError] = useState<string | null>(null);
  const [viewingReportIdx, setViewingReportIdx] = useState<number | null>(null);
  const [gridExpanded, setGridExpanded] = useState(true);
  const [tarExpanded, setTarExpanded] = useState(true);
  const [tarFocusOpen, setTarFocusOpen] = useState(false);
  const [aarDrawerOpen, setAarDrawerOpen] = useState(false);
  const [aarMeetingName, setAarMeetingName] = useState("");
  const [aarDate, setAarDate] = useState<import("dayjs").Dayjs | null>(null);
  const [aarSubmitting, setAarSubmitting] = useState(false);
  const navigate = useNavigate();
  const [contributorDrawer, setContributorDrawer] = useState<{
    open: boolean;
    contributorName: string;
    objectiveLabel: string;
    score: number | null;
    notes: {
      rationale?: string | null;
      whats_working?: string | null;
      challenges?: string | null;
      focus_ahead?: string | null;
    };
  } | null>(null);

  const loadGrid = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${periodType}/${periodKey}/grid`,
        { credentials: "include" }
      );
      if (r.ok) setGridData(await r.json());
    } finally {
      setIsLoading(false);
    }
  }, [sessionId, periodType, periodKey, isVisible]);

  const loadActivePeriodMeta = useCallback(async () => {
    if (!isAdvisorOrOwner) return;
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/progress-rounds`,
        { credentials: "include" }
      );
      if (r.ok) {
        const docs: PeriodMetaDoc[] = await r.json();
        const match = docs.find(
          (d) => d.periodType === periodType && d.periodKey === periodKey && d.status === "active"
        );
        setActivePeriodMeta(match ?? null);
      }
    } catch {
      // ignore
    }
  }, [sessionId, periodType, periodKey, isAdvisorOrOwner]);

  const loadSynthHistory = useCallback(async () => {
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${periodType}/${periodKey}/synthesis`,
        { credentials: "include" }
      );
      if (r.ok) setSynthHistory(await r.json());
    } catch { /* ignore */ }
  }, [sessionId, periodType, periodKey]);

  useEffect(() => {
    loadGrid();
    loadActivePeriodMeta();
    loadSynthHistory();
  }, [loadGrid, loadActivePeriodMeta, loadSynthHistory]);

  const handleSynthesize = async () => {
    setSynthStreaming(true);
    onSynthStateChange?.(true);
    setSynthText("");
    setSynthError(null);
    setViewingReportIdx(null);
    try {
      const r = await fetch(
        `${SERVER_URL}/api/rb/sessions/${sessionId}/contributions/${periodType}/${periodKey}/synthesize`,
        { method: "POST", credentials: "include" }
      );
      if (!r.ok || !r.body) { setSynthStreaming(false); onSynthStateChange?.(false); return; }
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.content) setSynthText((prev) => prev + payload.content);
            if (payload.done) {
              await loadSynthHistory();
              setSynthStreaming(false);
              onSynthStateChange?.(false);
              onReportCreated?.();
            }
            if (payload.error) { setSynthStreaming(false); onSynthStateChange?.(false); setSynthError(payload.error); }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setSynthStreaming(false);
      onSynthStateChange?.(false);
    }
  };

  // Trigger synthesis when parent bar's Synthesize button is clicked.
  // Use a ref to ignore the initial mount value — only fire on *new* clicks.
  const synthRequestRef = useRef(externalSynthRequest ?? 0);
  useEffect(() => {
    if (externalSynthRequest && externalSynthRequest > synthRequestRef.current) {
      synthRequestRef.current = externalSynthRequest;
      handleSynthesize();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalSynthRequest]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading team grid...</span>
      </div>
    );
  }

  if (!gridData || gridData.contributors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
        <Users className="h-6 w-6 text-muted-foreground/40" />
        <p className="text-sm font-medium">No team contributions yet</p>
        <p className="text-xs text-muted-foreground">
          Use "Notify Team" to invite contributors, or wait for team members to submit their progress.
        </p>
      </div>
    );
  }

  // Non-O/A users: when visibility is gated, show placeholder instead of any team content
  if (!isAdvisorOrOwner && !isVisible) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-6 text-center gap-3">
        <EyeOff className="h-6 w-6 text-muted-foreground/40" />
        <p className="text-sm font-medium text-foreground/60">Team data is not yet available</p>
        <p className="text-xs text-muted-foreground/50 max-w-xs">
          The advisor will share team contributions and synthesized insights when ready.
        </p>
      </div>
    );
  }

  const { wmbtItems, contributors, grid } = gridData;

  // synthHistory is sorted newest first from backend
  const displayReport = viewingReportIdx !== null ? synthHistory[viewingReportIdx] : (synthHistory[0] ?? null);

  // ── Grid stats helpers ─────────────────────────────────────────────────────
  const getScoresForRow = (idx: number): number[] =>
    contributors
      .map((c) => grid[String(idx)]?.[c.userId]?.pct_complete ?? null)
      .filter((s): s is number => s !== null);

  const teamAvg = (scores: number[]) =>
    scores.length === 0 ? null : scores.reduce((a, b) => a + b, 0) / scores.length;

  const teamStdDev = (scores: number[]) => {
    if (scores.length < 2) return null;
    const avg = teamAvg(scores)!;
    const variance = scores.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / scores.length;
    return Math.sqrt(variance);
  };

  const contribAvg = (c: { userId: string }) => {
    const scores = wmbtItems
      .map((_, idx) => grid[String(idx)]?.[c.userId]?.pct_complete ?? null)
      .filter((s): s is number => s !== null);
    return teamAvg(scores);
  };

  // Build objective rows mirroring the solo view's grouping logic exactly.
  // Items with categoryObjective set declare their parent objective; items without one
  // inherit the most recent categoryObjective seen (lastCatObj). Header items (no label)
  // are skipped. This collapses measurement sub-items into their parent objective row,
  // showing one scoreable row per objective and looking up scores at that objective's index.
  const _seenKeys = new Set<string>();
  let _lastCatObj: string | null = null;
  const objectiveRows: Array<{ item: typeof wmbtItems[0]; idx: number }> = [];
  wmbtItems.forEach((item, idx) => {
    const catObj = item.categoryObjective?.trim() ?? null;
    if (catObj) _lastCatObj = catObj;
    if (!item.label?.trim()) return; // skip header items
    const key = catObj || _lastCatObj || `${item.category}-${idx}`;
    if (!_seenKeys.has(key)) {
      _seenKeys.add(key);
      objectiveRows.push({ item: { ...item, categoryObjective: key }, idx });
    }
  });

  const formatScore = (score: number | null) => {
    if (score === null) return "—";
    return `${Math.round(score)}%`;
  };

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Section 1: Contributions Grid */}
      <div className="rounded-md border overflow-hidden">
        {/* Grid section header */}
        <div
          className="flex items-center px-3 py-2 bg-primary/5 border-b cursor-pointer hover:bg-primary/10 transition-colors select-none"
          onClick={() => setGridExpanded((v) => !v)}
        >
          <BarChart2 className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-xs font-semibold text-primary ml-1.5">Contributions Grid</span>
          <span className="text-[10px] text-primary/60 bg-primary/10 rounded px-1.5 py-0.5 ml-2">
            {contributors.length} contributor{contributors.length !== 1 ? "s" : ""}
          </span>
          <div className="flex-1" />
          {gridExpanded
            ? <ChevronUp className="h-3.5 w-3.5 text-primary/60" />
            : <ChevronDown className="h-3.5 w-3.5 text-primary/60" />
          }
        </div>
        {gridExpanded && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b">
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground min-w-[160px] sticky left-0 bg-slate-50 z-10">
                    Objective
                  </th>
                  {contributors.map((c) => (
                    <th key={c.userId} className="px-2 py-2 font-medium text-muted-foreground text-center min-w-[72px]">
                      <div className="truncate max-w-[72px]" title={c.name}>{c.name.split(" ")[0]}</div>
                    </th>
                  ))}
                  <th className="px-2 py-2 font-medium text-muted-foreground text-center min-w-[56px] border-l border-slate-200">
                    Avg
                  </th>
                  <th className="px-2 py-2 font-medium text-muted-foreground text-center min-w-[48px]">
                    ±σ
                  </th>
                </tr>
              </thead>
              <tbody>
                {objectiveRows.map(({ item, idx }) => {
                  const catStyle = getCategoryStyle(item.category);
                  const catLabel = getCategoryLabel(item.category);
                  const rowScores = getScoresForRow(idx);
                  const avg = teamAvg(rowScores);
                  const sd = teamStdDev(rowScores);
                  return (
                    <tr key={idx} className="border-b last:border-0 hover:bg-slate-50/50">
                      <td className="px-3 py-2 sticky left-0 bg-white">
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: catStyle.text }} />
                            <span className="text-[9px] font-semibold uppercase tracking-wide" style={{ color: catStyle.text }}>
                              {catLabel}
                            </span>
                          </div>
                          <span className="truncate max-w-[200px] font-medium text-sm" title={item.categoryObjective || item.label || ""}>
                            {item.categoryObjective || item.label || `Obj ${idx + 1}`}
                          </span>
                        </div>
                      </td>
                      {contributors.map((c) => {
                        const entry = grid[String(idx)]?.[c.userId];
                        const score = entry?.pct_complete ?? null;
                        const hasNotes = !!(entry?.rationale || entry?.whats_working || entry?.challenges || entry?.focus_ahead);
                        const display = score === null ? null : `${Math.round(score)}%`;
                        const objLabel = item.categoryObjective || item.label || `Obj ${idx + 1}`;
                        return (
                          <td key={c.userId} className="px-2 py-2 text-center">
                            {display === null ? (
                              <span className="text-muted-foreground/30">—</span>
                            ) : (
                              <div className="inline-flex flex-col items-center gap-0.5">
                                <Tag
                                  color={scoreBadgeColor(score, paceThreshold)}
                                  className="text-[10px] py-0 font-semibold cursor-pointer"
                                  onClick={() => setContributorDrawer({
                                    open: true,
                                    contributorName: c.name,
                                    objectiveLabel: objLabel,
                                    score: score,
                                    notes: {
                                      rationale: entry?.rationale,
                                      whats_working: entry?.whats_working,
                                      challenges: entry?.challenges,
                                      focus_ahead: entry?.focus_ahead,
                                    },
                                  })}
                                >
                                  {display}
                                </Tag>
                                {hasNotes && (
                                  <span className="w-1 h-1 rounded-full bg-primary/40 inline-block" title="Has notes" />
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                      <td className="px-2 py-2 text-center border-l border-slate-200">
                        {avg === null ? (
                          <span className="text-muted-foreground/30">—</span>
                        ) : (
                          <Tag color={scoreBadgeColor(avg, paceThreshold)} className="text-[10px] py-0 font-semibold">
                            {formatScore(avg)}
                          </Tag>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <span className="text-[10px] text-muted-foreground/70">
                          {sd === null ? "—" : `±${formatScore(sd)}`}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {/* Contributor avg row */}
                <tr className="border-t-2 border-slate-200 bg-slate-50/60">
                  <td className="px-3 py-2 sticky left-0 bg-slate-50/60">
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Avg</span>
                  </td>
                  {contributors.map((c) => {
                    const avg = contribAvg(c);
                    return (
                      <td key={c.userId} className="px-2 py-2 text-center">
                        {avg === null ? (
                          <span className="text-muted-foreground/30">—</span>
                        ) : (
                          <Tag color={scoreBadgeColor(avg, paceThreshold)} className="text-[10px] py-0 font-semibold">
                            {formatScore(avg)}
                          </Tag>
                        )}
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 border-l border-slate-200" />
                  <td className="px-2 py-2" />
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Section 2: Alignment Report */}
      <div className="rounded-md border overflow-hidden">
          {/* TAR section header */}
          <div
            className="flex items-center px-3 py-2 bg-primary/5 border-b cursor-pointer hover:bg-primary/10 transition-colors select-none"
            onClick={() => setTarExpanded((v) => !v)}
          >
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" />
            <span className="text-xs font-semibold text-primary ml-1.5">Progress: Team Alignment Report</span>
            {synthHistory.length > 0 && (
              <span className="text-[10px] text-primary/60 bg-primary/10 rounded px-1.5 py-0.5 ml-2 shrink-0">
                {synthHistory.length} version{synthHistory.length !== 1 ? "s" : ""}
              </span>
            )}
            {displayReport && (
              <span className="text-[10px] text-primary/60 ml-2 truncate min-w-0">
                · {formatRelativeTime(displayReport.generatedAt)}
                {displayReport.contributorNames && displayReport.contributorNames.length > 0 && (
                  <> · {displayReport.contributorNames.join(", ")}</>
                )}
              </span>
            )}
            <div className="flex-1" />
            <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
              {synthHistory.length > 1 && (
                <select
                  className="text-[10px] border rounded px-1 py-0.5 text-muted-foreground bg-white"
                  value={viewingReportIdx ?? 0}
                  onChange={(e) => setViewingReportIdx(Number(e.target.value))}
                >
                  {synthHistory.map((rpt, i) => (
                    <option key={i} value={i}>
                      {i === 0 ? "Latest" : `v${synthHistory.length - i}`} · {formatRelativeTime(rpt.generatedAt)}
                    </option>
                  ))}
                </select>
              )}
              {isAdvisorOrOwner && (
                <Button
                  size="small"
                  type={synthHistory.length === 0 ? "primary" : "default"}
                  icon={synthStreaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  onClick={handleSynthesize}
                  disabled={synthStreaming}
                  style={{ fontSize: 11 }}
                >
                  {synthStreaming ? "Running..." : synthHistory.length === 0 ? "Run with Vo" : "Re-run with Vo"}
                </Button>
              )}
              {displayReport?.reportMarkdown && (
                <Tooltip title="Download as PDF">
                  <span
                    role="button"
                    onClick={() => {
                      const pLabel = periodType === "weekly"
                        ? `Weekly — ${periodKey}`
                        : `Quarterly — ${periodKey.replace(/^(\d{4})-Q(\d)$/, "Q$2 FY$1")}`;
                      exportTarPdf(displayReport.reportMarkdown, {
                        companyName: companyName || "Roadmap Session",
                        periodLabel: pLabel,
                        contributorNames: displayReport.contributorNames,
                        generatedAt: displayReport.generatedAt,
                      });
                    }}
                    className="flex items-center cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
                  >
                    <Download className="h-3 w-3" />
                  </span>
                </Tooltip>
              )}
              {displayReport?.reportMarkdown && (
                <Tooltip title="Focus mode">
                  <span
                    role="button"
                    onClick={() => setTarFocusOpen(true)}
                    className="flex items-center cursor-pointer text-slate-300 hover:text-[#2c1561] transition-colors"
                  >
                    <Maximize2 className="h-3 w-3" />
                  </span>
                </Tooltip>
              )}
              {isAdvisorOrOwner && displayReport?.id && (
                <Tooltip title="Delete report">
                  <span
                    role="button"
                    onClick={() => {
                      Modal.confirm({
                        title: "Delete this report?",
                        content: "This will permanently remove the Team Alignment Report.",
                        okText: "Delete",
                        okButtonProps: { danger: true },
                        onOk: async () => {
                          try {
                            await fetch(
                              `${SERVER_URL}/api/rb/sessions/${sessionId}/progress-syntheses/${displayReport.id}`,
                              { method: "DELETE", credentials: "include" }
                            );
                            await loadSynthHistory();
                            setViewingReportIdx(null);
                            antdMessage.success("Report deleted");
                          } catch {
                            antdMessage.error("Failed to delete report");
                          }
                        },
                      });
                    }}
                    className="flex items-center cursor-pointer text-slate-300 hover:text-red-500 transition-colors"
                  >
                    <Trash2 className="h-3 w-3" />
                  </span>
                </Tooltip>
              )}
            </div>
            {tarExpanded
              ? <ChevronUp className="h-3.5 w-3.5 text-primary/60 ml-2 shrink-0" />
              : <ChevronDown className="h-3.5 w-3.5 text-primary/60 ml-2 shrink-0" />
            }
          </div>
          {tarExpanded && (
            synthStreaming && synthText ? (
              <div className="px-3 py-3 overflow-y-auto max-h-80">
                {renderSimpleMarkdown(synthText)}
                <span className="inline-block animate-pulse text-primary ml-1">▌</span>
              </div>
            ) : synthError ? (
              <div className="px-3 py-4">
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  {synthError === "No contributions found for this period"
                    ? "No progress data for this period yet. Enter scores above, then run the report."
                    : synthError}
                </p>
              </div>
            ) : displayReport ? (
              <div className="px-3 py-3 overflow-y-auto max-h-80">
                {renderSimpleMarkdown(displayReport.reportMarkdown)}
              </div>
            ) : !synthStreaming ? (
              <div className="px-3 py-6 text-center">
                <p className="text-xs text-muted-foreground">
                  {isAdvisorOrOwner
                    ? "No report yet. Click \"Run with Vo\" to generate a team alignment synthesis."
                    : "No team alignment report has been generated yet."}
                </p>
              </div>
            ) : null
          )}
        </div>

      {/* TAR focus mode modal */}
      {displayReport?.reportMarkdown && (
        <Modal
          open={tarFocusOpen}
          onCancel={() => setTarFocusOpen(false)}
          footer={null}
          width={720}
          title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Progress: Team Alignment Report</span>}
        >
          <div className="text-sm text-foreground/80 leading-relaxed max-h-[70vh] overflow-y-auto py-2">
            {renderSimpleMarkdown(displayReport.reportMarkdown)}
          </div>
        </Modal>
      )}

      {/* Section 2b: After Action Review trigger — advisor/owner only */}
      {isAdvisorOrOwner && (
        <div className="rounded-md border overflow-hidden">
          <div className="flex items-center px-3 py-2 bg-slate-50 border-b">
            <ClipboardList className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <span className="text-xs font-semibold text-muted-foreground ml-1.5">After Action Review</span>
          </div>
          <div className="px-3 py-3 space-y-2">
            <p className="text-xs text-muted-foreground leading-relaxed">
              Capture lessons learned from this period with a structured retrospective.
              AARs created here are linked to this roadmap session.
            </p>
            <Button
              size="small"
              icon={<ClipboardList className="h-3 w-3" />}
              onClick={() => {
                setAarMeetingName(`${periodLabel} — After Action Review`);
                setAarDate(dayjs());
                setAarDrawerOpen(true);
              }}
              style={{ fontSize: 11 }}
            >
              Start an AAR
            </Button>
          </div>
        </div>
      )}

      {/* AAR creation drawer */}
      <Drawer
        open={aarDrawerOpen}
        onClose={() => setAarDrawerOpen(false)}
        title="Start an After Action Review"
        width={380}
        placement="right"
        footer={
          <div className="flex justify-end gap-2">
            <Button onClick={() => setAarDrawerOpen(false)} size="small">
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              loading={aarSubmitting}
              disabled={!aarMeetingName.trim() || !aarDate}
              onClick={async () => {
                if (!aarMeetingName.trim() || !aarDate) return;
                setAarSubmitting(true);
                try {
                  const res = await fetch(`${SERVER_URL}/api/aar/sessions`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({
                      meeting_name: aarMeetingName.trim(),
                      meeting_date: aarDate.format("YYYY-MM-DD"),
                      mode: "solo",
                      roadmap_session_id: sessionId,
                    }),
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setAarDrawerOpen(false);
                    navigate(`/practices/aar?session=${data.session.id}`);
                  }
                } finally {
                  setAarSubmitting(false);
                }
              }}
            >
              Create & Open
            </Button>
          </div>
        }
      >
        <Form layout="vertical" size="small">
          <Form.Item label="AAR name" required>
            <Input
              value={aarMeetingName}
              onChange={(e) => setAarMeetingName(e.target.value)}
              placeholder="e.g. Q1 Weekly Review — After Action"
            />
          </Form.Item>
          <Form.Item label="Date" required>
            <DatePicker
              value={aarDate}
              onChange={(v) => setAarDate(v)}
              style={{ width: "100%" }}
              format="MMM D, YYYY"
            />
          </Form.Item>
          <p className="text-xs text-muted-foreground">
            This AAR will be linked to this roadmap session and accessible from Studio.
          </p>
        </Form>
      </Drawer>

      {/* Section 3: Team Scores — gated by session-level visibility toggle */}
      {displayReport?.perObjective && displayReport.perObjective.length > 0 && (
        <div className="rounded-md border overflow-hidden">
          <div className="flex items-center gap-1.5 px-3 py-2 bg-slate-50 border-b">
            <BarChart2 className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-semibold text-muted-foreground">Team Progress Scores</span>
            <span className="text-[10px] text-muted-foreground/60">· from latest synthesis</span>
          </div>
          <div className="divide-y">
            {displayReport.perObjective.map((obj) => (
              <div key={obj.wmbt_index} className="px-3 py-2.5 flex gap-3 items-start">
                <div className="shrink-0 pt-0.5">
                  <Tag
                    color={scoreBadgeColor(obj.team_score, paceThreshold)}
                    className="text-[11px] font-semibold py-0"
                  >
                    {`${Math.round(obj.team_score)}%`}
                  </Tag>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground truncate">{obj.objective_label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{obj.team_summary}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contributor drawer */}
      {contributorDrawer && (
        <ContributorReportDrawer
          open={contributorDrawer.open}
          onClose={() => setContributorDrawer(null)}
          contributorName={contributorDrawer.contributorName}
          periodLabel={periodLabel}
          periodType={periodType}
          objectiveLabel={contributorDrawer.objectiveLabel}
          score={contributorDrawer.score}
          notes={contributorDrawer.notes}
          paceThreshold={paceThreshold}
        />
      )}
    </div>
  );
}

// ── Notify Team Modal ─────────────────────────────────────────────────────────

function buildDefaultMessage(
  periodType: string,
  periodLabel: string,
  companyName: string,
): string {
  if (periodType === "qbr") {
    return (
      `Please submit your ${periodLabel} quarterly scores and rationale. ` +
      `Your scores and notes will be used to generate a team alignment report for ${companyName}.`
    );
  }
  return (
    `Please drop in your ${periodLabel} progress updates — add scores and notes for each ` +
    `objective and save anytime.`
  );
}

function NotifyTeamModal({
  open,
  onClose,
  sessionId,
  activeTab,
  companyName,
  yearStartMonth,
}: {
  open: boolean;
  onClose: () => void;
  sessionId: string;
  activeTab: string;
  companyName: string;
  yearStartMonth: number;
}) {
  const [emails, setEmails] = useState("");
  const [deadline, setDeadline] = useState<dayjs.Dayjs | null>(null);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [teams, setTeams] = useState<{ id: string; name: string; members: { email: string }[] }[]>([]);

  const periodType = activeTab === "qbr" ? "qbr" : "weekly";

  const now = new Date();
  const year = now.getFullYear();
  const jan4 = new Date(year, 0, 4);
  const isoWeekday = (jan4.getDay() + 6) % 7;
  const mondayOfW1 = new Date(jan4);
  mondayOfW1.setDate(jan4.getDate() - isoWeekday);
  const diffWeeks = Math.floor((now.getTime() - mondayOfW1.getTime()) / (7 * 86400000));
  const currentPeriodKey = periodType === "weekly"
    ? `${year}-W${String(diffWeeks + 1).padStart(2, "0")}`
    : (() => {
        const m = now.getMonth() + 1;
        // fiscal quarter based on yearStartMonth
        const adjustedM = ((m - yearStartMonth + 12) % 12) + 1;
        const q = Math.ceil(adjustedM / 3);
        return `${year}-Q${q}`;
      })();

  const periodLabel = periodType === "weekly"
    ? currentPeriodKey.replace("-W", " W")
    : currentPeriodKey.replace("-Q", " Q");

  // Pre-fill message and fetch teams when modal opens
  useEffect(() => {
    if (!open) return;
    setMessage(buildDefaultMessage(periodType, periodLabel, companyName));
    setEmailError(null);
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/participant-teams`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then(setTeams)
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSend = async () => {
    const rawEmails = emails.split(/[\n,]+/).map((e) => e.trim()).filter(Boolean);
    if (!rawEmails.length) return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalid = rawEmails.filter((e) => !emailRegex.test(e));
    if (invalid.length) {
      setEmailError(`Invalid email${invalid.length > 1 ? "s" : ""}: ${invalid.join(", ")}`);
      return;
    }
    setEmailError(null);
    setSending(true);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/progress-rounds`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          period_type: periodType,
          period_key: currentPeriodKey,
          participants: rawEmails.map((email) => ({ email })),
          deadline: deadline ? deadline.toISOString() : null,
          personal_message: message.trim() || null,
          send_notifications: true,
        }),
      });
      if (r.ok) {
        setSent(true);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4 text-primary" />
          <span>Notify Team — {periodType === "qbr" ? "Quarterly" : "Weekly"} Progress</span>
        </div>
      }
      footer={null}
      width={480}
    >
      {sent ? (
        <div className="py-8 text-center flex flex-col items-center gap-4">
          <p className="text-sm font-semibold text-emerald-600">Invites sent!</p>
          <p className="text-xs text-muted-foreground">
            Contributors will receive an email with their personal link.
          </p>
          <Button
            type="primary"
            onClick={() => {
              setSent(false);
              setEmails("");
              setDeadline(null);
              setMessage("");
              onClose();
            }}
          >
            Done
          </Button>
        </div>
      ) : (
        <div className="space-y-4 pt-2">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-muted-foreground">
                Contributor emails{" "}
                <span className="font-normal">(one per line or comma-separated)</span>
              </label>
            </div>
            {teams.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-1.5">
                {teams.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => {
                      const teamEmails = t.members.map((m) => m.email).filter(Boolean);
                      if (!teamEmails.length) return;
                      const existing = emails.split(/[\n,]+/).map((e) => e.trim()).filter(Boolean);
                      const merged = Array.from(new Set([...existing, ...teamEmails]));
                      setEmails(merged.join("\n"));
                    }}
                    style={{
                      fontSize: 10,
                      padding: "2px 7px",
                      borderRadius: 999,
                      border: "1px solid #c4b5fd",
                      background: "#f5f3ff",
                      color: "#6d28d9",
                      cursor: "pointer",
                      lineHeight: "16px",
                    }}
                  >
                    + {t.name} ({t.members.length})
                  </button>
                ))}
              </div>
            )}
            <Input.TextArea
              value={emails}
              onChange={(e) => { setEmails(e.target.value); setEmailError(null); }}
              placeholder={"alice@company.com\nbob@company.com"}
              autoSize={{ minRows: 3, maxRows: 6 }}
              className="text-sm font-mono"
              status={emailError ? "error" : undefined}
            />
            {emailError && (
              <p style={{ fontSize: 11, color: "#ef4444", marginTop: 4 }}>{emailError}</p>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">
              Deadline <span className="font-normal">(optional — included in email automatically)</span>
            </label>
            <DatePicker
              showTime={{ use12Hours: true, format: "h:mm A" }}
              format="MMM D, YYYY h:mm A"
              value={deadline}
              onChange={setDeadline}
              className="w-full"
              placeholder="Select deadline…"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground block mb-1">
              Message to team
            </label>
            <Input.TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              autoSize={{ minRows: 3, maxRows: 6 }}
              className="text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button onClick={onClose} disabled={sending}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleSend}
              loading={sending}
              disabled={!emails.trim()}
              icon={<Bell className="h-3.5 w-3.5" />}
            >
              Send invites
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function ProgressReflectionPanel({
  sessionId,
  buildingBlocks,
  yearStartMonth,
  isAdvisorOrOwner = false,
  currentUserId,
  companyName = "",
  defaultProgressTab,
  onReportCreated,
  onSendToMainChat,
  onVisibilityChange,
  visibilityMap: initialVisMap,
  viewMode = "solo",
  onViewModeChange,
  showViewToggle = false,
}: ProgressReflectionPanelProps) {
  const validTabs = ["weekly", "qbr", "annual"];
  const [activeTab, setActiveTab] = useState(
    defaultProgressTab && validTabs.includes(defaultProgressTab) ? defaultProgressTab : "weekly"
  );
  // Per-period visibility map — replaces old ProgressSettings
  const [visMap, setVisMap] = useState<VisibilityMap>(initialVisMap ?? {});
  // Track active period key + status from the current PeriodTab
  const [activePeriodKey, setActivePeriodKey] = useState<string>("");
  const [activePeriodStatus, setActivePeriodStatus] = useState<"locked" | "active" | "planning" | null>(null);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [synthRequest, setSynthRequest] = useState(0);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const [planView, setPlanView] = useState(false);

  // Sync with parent's visibility map when it changes
  useEffect(() => {
    if (initialVisMap) setVisMap(initialVisMap);
  }, [initialVisMap]);

  // Fetch the visibility map on mount
  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (data) setVisMap(data); })
      .catch(() => {});
  }, [sessionId]);

  const handleVisibilityToggle = async () => {
    if (!activePeriodKey) return;
    const periodType = activeTab === "qbr" ? "qbr" : "weekly";
    const mapKey = `${periodType}:${activePeriodKey}`;
    const defaultVis = (periodType === "weekly" && activePeriodStatus === "active") ? "open" : "gated";
    const currentVis = visMap[mapKey] ?? defaultVis;
    const newVis = currentVis === "open" ? "gated" : "open";
    const updatedMap = { ...visMap, [mapKey]: newVis as "open" | "gated" };
    setVisMap(updatedMap);
    try {
      const r = await fetch(`${SERVER_URL}/api/rb/sessions/${sessionId}/visibility`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ period_type: periodType, period_key: activePeriodKey, visibility: newVis }),
      });
      if (r.ok) {
        const serverMap = await r.json();
        setVisMap(serverMap);
        onVisibilityChange?.(serverMap);
      }
    } catch { /* ignore */ }
  };

  const periodType = activeTab === "qbr" ? "qbr" : "weekly";
  const defaultVis = (periodType === "weekly" && activePeriodStatus === "active") ? "open" : "gated";
  const currentVisibility = activePeriodKey
    ? (visMap[`${periodType}:${activePeriodKey}`] ?? defaultVis)
    : "gated";

  const progressSubTabs = [
    { key: "weekly", Icon: TrendingUp, label: "Weekly" },
    { key: "qbr", Icon: BarChart2, label: "Quarterly" },
    { key: "annual", Icon: CalendarClock, label: "Annual" },
  ] as const;

  const progressDescription =
    activeTab === "weekly"
      ? "Track & reflect on weekly progress against your quarterly objectives."
      : activeTab === "qbr"
      ? "Track & reflect on quarterly progress against your annual objectives."
      : "Annual review and reflection on your roadmap trajectory.";

  // Format the period key for human-readable tooltip
  const periodLabel = activePeriodKey
    ? (periodType === "weekly" ? activePeriodKey.replace("-W", " W") : activePeriodKey.replace("-Q", " Q"))
    : "";

  // Config-driven O/A controls for the ConsensusBuilderBar.
  // Team-only controls (visibility / notify / synthesize) are suppressed in
  // State 1 (single-user roadmaps, showViewToggle === false) — they must only
  // appear once the roadmap has 2+ participants (State 2).
  const progressControls: CommandCenterConfig | undefined = (activeTab !== "annual" && showViewToggle) ? {
    visibility: {
      isVisible: currentVisibility === "open",
      onToggle: handleVisibilityToggle,
      tooltipVisible: periodLabel
        ? `Team data for ${periodLabel} visible to contributors — click to hide`
        : "Team data visible to contributors — click to hide",
      tooltipHidden: periodLabel
        ? `Team data for ${periodLabel} hidden from contributors — click to show`
        : "Team data hidden from contributors — click to show",
    },
    notify: { onClick: () => setNotifyOpen(true) },
    synthesize: { onClick: () => setSynthRequest((r) => r + 1), isSynthesizing },
  } : undefined;

  // Sub-tabs rendered as leftContent in ConsensusBuilderBar
  const progressSubTabsContent = (
    <div className="flex items-center gap-0">
      {progressSubTabs.map(({ key, Icon, label }, i) => (
        <span key={key} className="flex items-center">
          {i > 0 && <span className="text-slate-300 mx-2 text-[11px] select-none">|</span>}
          <button
            onClick={() => { setActiveTab(key); setPlanView(false); }}
            className={`border-0 bg-transparent outline-none cursor-pointer p-0 text-[12px] font-medium transition-colors flex items-center gap-1 ${
              activeTab === key && !planView
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </button>
        </span>
      ))}
      {activeTab === "qbr" && (
        <>
          <span className="text-slate-300 mx-2 text-[11px] select-none">|</span>
          <button
            onClick={() => setPlanView((v) => !v)}
            className={`border-0 bg-transparent outline-none cursor-pointer p-0 text-[12px] font-medium transition-colors flex items-center gap-1 ${
              planView ? "text-primary" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <ClipboardList className="h-3 w-3" />
            Plan
          </button>
        </>
      )}
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Command Center bar — sub-tabs (left) + O/A controls + Solo/Team toggle (right) */}
      <ConsensusBuilderBar
        isAdvisorOrOwner={isAdvisorOrOwner}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        showToggle={showViewToggle}
        toggleTestId="rb-progress-view-toggle"
        controls={progressControls}
        leftContent={progressSubTabsContent}
        className="px-4 py-2 bg-white"
      />

      {/* Header below strip */}
      <div className="shrink-0 px-3 pt-3 pb-2 bg-white border-b border-slate-100">
        <div className="text-base font-semibold leading-snug">Progress & Reflection</div>
        <p className="text-xs text-muted-foreground mt-0.5">{progressDescription}</p>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0">
        {/* PAP Plan view — shown when Plan toggle is active */}
        {planView && activeTab === "qbr" && (
          <PAPPlanView
            sessionId={sessionId}
            periodType="qbr"
            isAdvisorOrOwner={isAdvisorOrOwner}
          />
        )}
        {!planView && activeTab === "weekly" && (
          <PeriodTab
            sessionId={sessionId}
            buildingBlocks={buildingBlocks}
            periodType="weekly"
            yearStartMonth={yearStartMonth}
            isAdvisorOrOwner={isAdvisorOrOwner}
            isVisible={currentVisibility === "open"}
            currentUserId={currentUserId}
            viewMode={viewMode}
            companyName={companyName}
            externalSynthRequest={synthRequest}
            onSynthStateChange={setIsSynthesizing}
            onReportCreated={onReportCreated}
            onSendToChat={onSendToMainChat}
            onActivePeriodChange={setActivePeriodKey}
            onPeriodStatusChange={setActivePeriodStatus}
          />
        )}
        {!planView && activeTab === "qbr" && (
          <PeriodTab
            sessionId={sessionId}
            buildingBlocks={buildingBlocks}
            periodType="qbr"
            yearStartMonth={yearStartMonth}
            isAdvisorOrOwner={isAdvisorOrOwner}
            isVisible={currentVisibility === "open"}
            currentUserId={currentUserId}
            viewMode={viewMode}
            companyName={companyName}
            externalSynthRequest={synthRequest}
            onSynthStateChange={setIsSynthesizing}
            onReportCreated={onReportCreated}
            onSendToChat={onSendToMainChat}
            onActivePeriodChange={setActivePeriodKey}
            onPeriodStatusChange={setActivePeriodStatus}
          />
        )}
        {activeTab === "annual" && (
          <ComingSoonTab
            title="Annual Review"
            description="Annual review mode is coming in a future update. It will enable structured evaluation of each block's trajectory and Vo-assisted redraft sessions for a new season of your roadmap."
          />
        )}
      </div>

      <NotifyTeamModal
        open={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        sessionId={sessionId}
        activeTab={activeTab}
        companyName={companyName}
        yearStartMonth={yearStartMonth}
      />

    </div>
  );
}
